import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import authApi from "../../../../service/IdApi";
import { useAppContext } from "../../../../context/appContext";

const STATUS_SEVERITY = {
    active: "info",
    completed: "success",
    snoozed: "warning",
    dismissed: "danger",
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString();
};

const ProviderRecommendationsDialog = ({ visible, onHide, providerId, providerName }) => {
    const { displayAlert } = useAppContext();
    const [recommendations, setRecommendations] = useState(null);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!visible || !providerId) return;
        let cancelled = false;
        setIsLoading(true);
        setRecommendations(null);
        setUsers([]);
        Promise.all([
            authApi.get("admin/recommendations/providers/" + encodeURIComponent(providerId)),
            authApi.post("admin/provider/users", { providerId }),
        ])
            .then(([recommendationsResult, usersResult]) => {
                if (!cancelled) {
                    setRecommendations(recommendationsResult.data.recommendations || []);
                    setUsers(usersResult.data.users || []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    displayAlert("error", "Error", "Failed to load provider details");
                    console.error(err);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [visible, providerId]);

    const emailBodyTemplate = (rowData) => {
        if (!rowData.email) return "—";
        return (
            <a href={`/account/admin/user-events?email=${rowData.email}`}>
                {rowData.email}
            </a>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const status = rowData.status;
        const severity = STATUS_SEVERITY[status] || null;
        return <Tag value={status || "—"} severity={severity} />;
    };

    const effortBodyTemplate = (rowData) => `${rowData.value} / ${rowData.priority} / ${rowData.effort}`;

    const dateBodyTemplate = (rowData) => {
        if (rowData.status === "snoozed") return formatDate(rowData.snoozedUntil);
        if (rowData.status === "completed") return formatDate(rowData.completedAt);
        if (rowData.status === "dismissed") return formatDate(rowData.dismissedAt);
        return "—";
    };

    const prerequisitesBodyTemplate = (rowData) => {
        if (!rowData.prerequisites || rowData.prerequisites.length === 0) return "—";
        return rowData.prerequisites.join(", ");
    };

    if (!visible) return null;

    return (
        <Dialog
            visible={visible}
            style={{ width: "70vw" }}
            breakpoints={{ "1280px": "85vw", "641px": "95vw" }}
            header={providerName ? `Recommendations - ${providerName}` : "Recommendations"}
            modal
            onHide={onHide}
        >
            {isLoading ? (
                <div className="flex justify-content-center py-4">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
                </div>
            ) : recommendations ? (
                <>
                    <div className="field grid mb-2">
                        <label className="col-fixed font-semibold" style={{ width: "150px" }}>Account Users</label>
                        <div className="col text-color-secondary">
                            Recommendation emails are sent to the admin user with the oldest account created date.
                        </div>
                    </div>
                    <DataTable
                        value={users}
                        dataKey="email"
                        size="small"
                        stripedRows
                        className="p-datatable-sm mb-4"
                        emptyMessage="No users on this account."
                    >
                        <Column field="userId" header="ID" sortable />
                        <Column field="email" header="Email" body={emailBodyTemplate} sortable />
                        <Column field="createdAt" header="Created On" sortable />
                    </DataTable>
                    <div className="field grid mb-2">
                        <label className="col-fixed font-semibold" style={{ width: "150px" }}>Recommendations</label>
                    </div>
                    <DataTable
                        value={recommendations}
                        size="small"
                        stripedRows
                        className="p-datatable-sm"
                        emptyMessage="No recommendations for this provider."
                    >
                        <Column field="title" header="Title" />
                        <Column
                            field="product"
                            header="Product"
                            body={(rowData) => rowData.productFriendlyName || rowData.product || "—"}
                        />
                        <Column header="Status" body={statusBodyTemplate} />
                        <Column field="score" header="Score" />
                        <Column header="Value / Priority / Effort" body={effortBodyTemplate} />
                        <Column header="Date" body={dateBodyTemplate} />
                        <Column header="Emailed At" body={(rowData) => formatDate(rowData.emailedAt)} />
                        <Column header="Prerequisites" body={prerequisitesBodyTemplate} />
                    </DataTable>
                </>
            ) : null}
        </Dialog>
    );
};

export default ProviderRecommendationsDialog;
